import { useIsMutating } from '@eigtech/ui-shared-api'
import { mostReadable, stringToColor } from '@eigtech/ui-shared-color'
import { AvatarFallback, Avatar as BaseAvatar, getInitials } from '@eigtech/ui/components/avatar'
import { Button } from '@eigtech/ui/components/button'
import { Spinner } from '@eigtech/ui/components/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { ellipsis } from '@eigtech/ui/lib/common'
import { cn } from '@eigtech/ui/lib/utils'
import {
  useApplyVideoObjectFit,
  useAudioVideo,
  useLocalVideo,
  useRemoteVideoTileState,
} from 'amazon-chime-sdk-component-library-react'
import { Camera } from 'lucide-react'
import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/shallow'
import { meetingsMutationKeys } from '../../api/constants'
import { MeetingState, useMeetingStore } from '../../context/MeetingState'
import { useCaptureImageFromVideo } from '../../hooks/useCaptureImageFromVideo'
import { useFilteredRoster } from '../../hooks/useRoster'
import { MicActivity as BaseMicActivity } from '../Status/MicActivity'

type TileContextType = {
  attendeeId: string | undefined
  isActiveVideo: boolean | undefined
  isLocal: boolean
  name: string | undefined
  tileId: number | null
  videoRef: React.Ref<HTMLVideoElement> | undefined
}

const TileContext = createContext<TileContextType>({
  attendeeId: undefined,
  isActiveVideo: undefined,
  isLocal: false,
  name: undefined,
  tileId: null,
  videoRef: undefined,
})

const chimeAttendeeStateSelector = (state: MeetingState) => state.chimeAttendee
const canScreenshotStateSelector = (state: MeetingState) => state.canScreenshot
const meetingDetailsStateSelector = (state: MeetingState) => state.meetingDetails

function VideoTileRoot({
  attendeeId: attendeeIdProp,
  children,
  isActiveVideo,
  name: fallbackName,
}: {
  attendeeId?: string
  children?: React.ReactNode
  isActiveVideo?: boolean
  name?: string
}) {
  const chimeAttendee = useMeetingStore(useShallow(chimeAttendeeStateSelector))
  const roster = useFilteredRoster()

  const { tileId: localTileId, isVideoEnabled } = useLocalVideo()
  const { attendeeIdToTileId } = useRemoteVideoTileState()

  const { attendeeId, isLocal, tileId } = useMemo(
    () =>
      attendeeIdProp
        ? { attendeeId: attendeeIdProp, isLocal: false, tileId: attendeeIdToTileId[attendeeIdProp] }
        : { attendeeId: chimeAttendee?.AttendeeId, isLocal: true, tileId: localTileId },
    [attendeeIdProp, attendeeIdToTileId, chimeAttendee?.AttendeeId, localTileId]
  )

  const name = useMemo(() => {
    const normalizedAttendeeId = attendeeId?.replace('#content', '')
    const name =
      (!!normalizedAttendeeId && roster[normalizedAttendeeId.replace('#content', '')]?.name) ??
      fallbackName

    return name || undefined
  }, [attendeeId, fallbackName, roster])

  const audioVideo = useAudioVideo()
  const videoRef = useRef<HTMLVideoElement>(null!)
  useApplyVideoObjectFit(videoRef)

  useEffect(() => {
    if (!audioVideo || !tileId || !videoRef.current || (isLocal && !isVideoEnabled)) {
      return
    }

    audioVideo.bindVideoElement(tileId, videoRef.current)

    return () => {
      const tile = audioVideo.getVideoTile(tileId)
      if (tile) {
        audioVideo.unbindVideoElement(tileId)
      }
    }
  }, [audioVideo, tileId, isVideoEnabled, isLocal])

  return (
    <TileContext.Provider value={{ attendeeId, isActiveVideo, isLocal, name, tileId, videoRef }}>
      {children}
    </TileContext.Provider>
  )
}

function VideoTileVideo({ className }: { className?: string }) {
  const { videoRef } = useContext(TileContext)
  return <video ref={videoRef} className={cn('h-full w-full object-contain', className)} />
}

function VideoTileAvatar({ className }: { className?: string }) {
  const { name = 'Unknown' } = useContext(TileContext)
  const color = stringToColor(name)
  const fontColor = mostReadable(color, ['#fff', '#000'])?.toHexString()

  return (
    <BaseAvatar
      className={cn('size-sm', className)}
      style={{ '--bg-color': color, '--font-color': fontColor } as React.CSSProperties}
    >
      <AvatarFallback className="bg-[var(--bg-color)] text-[var(--font-color)]">
        {getInitials(name)}
      </AvatarFallback>
    </BaseAvatar>
  )
}

function VideoTileName({ className }: { className?: string }) {
  const { name } = useContext(TileContext)

  return (
    !!name && (
      <Tooltip>
        <TooltipTrigger>
          <p className={cn('text-[0.75em] text-white', ellipsis, className)}>{name}</p>
        </TooltipTrigger>

        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    )
  )
}

function VideoTileMicActivity(props: { className?: string; iconClassName?: string }) {
  const { attendeeId } = useContext(TileContext)

  return !!attendeeId && <BaseMicActivity {...props} attendeeId={attendeeId} />
}

function VideoTileScreenshot() {
  const { isActiveVideo, isLocal, tileId } = useContext(TileContext)
  const canScreenshot = useMeetingStore(canScreenshotStateSelector)

  if (!canScreenshot || tileId === null) {
    return null
  }

  return <InnerScreenshot isActiveVideo={isActiveVideo} isLocal={isLocal} tileId={tileId} />
}

function InnerScreenshot({
  tileId,
  isLocal,
  isActiveVideo,
}: {
  tileId: number
  isLocal?: boolean
  isActiveVideo?: boolean
}) {
  const meetingDetails = useMeetingStore(useShallow(meetingDetailsStateSelector))

  const { canvasRef, downloadRef, capturePhoto } = useCaptureImageFromVideo({ tileId })

  const isUploading = useIsMutating({
    mutationKey: meetingsMutationKeys.uploadScreenshot({
      entityId: meetingDetails?.entityId ?? 'corn:unknown:unknown:unknown',
      meetingId: meetingDetails?.meetingId ?? '',
    }),
  })
  return (
    <>
      {!!isUploading && <Spinner className="size-[1.1em]" />}

      <Tooltip>
        <TooltipTrigger
          render={(props) =>
            isLocal && isActiveVideo ? (
              <Button
                {...props}
                aria-label="Capture photo."
                className="absolute bottom-1 right-1.5 size-[3em] shadow-2xl"
                mode="icon"
                onClick={capturePhoto}
              >
                <Camera className="inherit size-[1.75em] text-[inherit]" />
              </Button>
            ) : (
              <Button
                {...props}
                aria-label="Capture photo."
                className="size-[1.75em] text-white shadow-2xl"
                mode="icon"
                size="sm"
                variant="ghost"
                onClick={capturePhoto}
              >
                <Camera className="inherit size-[1.25em] text-[inherit]" />
              </Button>
            )
          }
        ></TooltipTrigger>

        <TooltipContent>Capture photo</TooltipContent>
      </Tooltip>

      <canvas ref={canvasRef} className="hidden" />
      <a ref={downloadRef} className="hidden" />
    </>
  )
}

export {
  VideoTileAvatar,
  VideoTileMicActivity,
  VideoTileName,
  VideoTileRoot,
  VideoTileScreenshot,
  VideoTileVideo,
}
